import { getDBAsync } from '@/db';
import type { Game, GameWithTotals, Round, GameStatus, Match } from '@/types';
import { generateId } from '@/lib/utils';
import { calculateTotals } from '@/lib/scoring';
import { roundRepo } from './roundRepo';

export const gameRepo = {
  /**
   * Get all games
   */
  async getAll(): Promise<Game[]> {
    const db = await getDBAsync();
    return db.getAll('games');
  },

  /**
   * Get a game by ID
   */
  async getById(id: string): Promise<Game | undefined> {
    const db = await getDBAsync();
    return db.get('games', id);
  },

  /**
   * Get a game with rounds and calculated totals
   */
  async getWithTotals(id: string): Promise<GameWithTotals | null> {
    const game = await this.getById(id);
    if (!game) {
      return null;
    }

    const rounds = await roundRepo.getByGameId(id);
    const totals = calculateTotals(game.participants, rounds);

    return {
      ...game,
      rounds,
      totals,
    };
  },

  /**
   * Get all games for a match (sorted by game number)
   */
  async getByMatchId(matchId: string): Promise<Game[]> {
    const db = await getDBAsync();
    const games = await db.getAllFromIndex('games', 'by-match', matchId);
    return games.sort((a, b) => a.gameNumber - b.gameNumber);
  },

  /**
   * Get games by status
   */
  async getByStatus(status: GameStatus): Promise<Game[]> {
    const db = await getDBAsync();
    return db.getAllFromIndex('games', 'by-status', status);
  },

  /**
   * Get active games
   */
  async getActive(): Promise<Game[]> {
    return this.getByStatus('active');
  },

  /**
   * Get completed games (sorted by completion date, newest first)
   */
  async getCompleted(): Promise<Game[]> {
    const games = await this.getByStatus('completed');
    return games.sort(
      (a, b) =>
        (b.completedAt?.getTime() ?? 0) - (a.completedAt?.getTime() ?? 0)
    );
  },

  /**
   * Create a new game for a match
   */
  async createForMatch(match: Match): Promise<Game> {
    const db = await getDBAsync();

    // Get existing games to determine game number
    const existingGames = await this.getByMatchId(match.id);
    const gameNumber = existingGames.length + 1;

    const game: Game = {
      id: generateId(),
      matchId: match.id,
      gameNumber,
      mode: match.mode,
      targetScore: match.targetScore,
      status: 'active',
      participants: match.participants,
      createdAt: new Date(),
    };

    await db.put('games', game);
    return game;
  },

  /**
   * Update a game
   */
  async update(id: string, updates: Partial<Game>): Promise<Game | null> {
    const db = await getDBAsync();
    const existing = await db.get('games', id);
    if (!existing) {
      return null;
    }

    const updated: Game = {
      ...existing,
      ...updates,
    };
    await db.put('games', updated);
    return updated;
  },

  /**
   * Complete a game with a winner
   */
  async complete(id: string, winnerId: string): Promise<Game | null> {
    return this.update(id, {
      status: 'completed',
      winnerId,
      completedAt: new Date(),
    });
  },

  /**
   * Delete a game and its rounds
   */
  async delete(id: string): Promise<boolean> {
    const db = await getDBAsync();
    const existing = await db.get('games', id);
    if (!existing) {
      return false;
    }

    // Delete all rounds for this game
    const rounds = await roundRepo.getByGameId(id);
    for (const round of rounds) {
      await db.delete('rounds', round.id);
    }

    // Delete the game
    await db.delete('games', id);
    return true;
  },

  /**
   * Get recent games (both active and completed)
   */
  async getRecent(limit: number = 10): Promise<Game[]> {
    const db = await getDBAsync();
    const all = await db.getAll('games');
    return all
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
      .slice(0, limit);
  },

  /**
   * Get game count by status
   */
  async countByStatus(status: GameStatus): Promise<number> {
    const games = await this.getByStatus(status);
    return games.length;
  },

  /**
   * Get all games with their rounds (for statistics)
   */
  async getAllWithRounds(): Promise<Array<{ game: Game; rounds: Round[] }>> {
    const games = await this.getAll();
    const result: Array<{ game: Game; rounds: Round[] }> = [];

    for (const game of games) {
      const rounds = await roundRepo.getByGameId(game.id);
      result.push({ game, rounds });
    }

    return result;
  },

  /**
   * Get active game for a match (if any)
   */
  async getActiveForMatch(matchId: string): Promise<Game | null> {
    const games = await this.getByMatchId(matchId);
    return games.find((g) => g.status === 'active') || null;
  },

  /**
   * Count completed games for a match
   */
  async countCompletedForMatch(matchId: string): Promise<number> {
    const games = await this.getByMatchId(matchId);
    return games.filter((g) => g.status === 'completed').length;
  },
};
